import {
  AddToCartButton,
  ProductOptionsProvider,
  useProductOptions,
  Image,
  Link,
  useCart,
} from '@shopify/hydrogen';
import {Product as ProductType} from '@shopify/hydrogen/storefront-api-types';
import {FC, useEffect, useMemo, useState} from 'react';
import {format} from 'date-fns';
import Carousel from 'nuka-carousel';
import Calendar from 'react-calendar';
import {useForm} from 'react-hook-form';
import clsx from 'clsx';

import {
  Gallery,
  ProductDetailReview,
  ProductReview,
  ProductDetailBanner,
  DefaultModal,
  ProductRecommendation,
  ProductAssurance,
  GiftIcon,
  useAppContext,
  RefreshingTimeslots,
  CheckboxField,
  ProductDescriptionTab,
  SparkleIcon,
  ProductOptions,
} from '~/components';
import {useDrawerContext} from '~/components/contexts';
import {ChevronLeft, ChevronRight} from 'react-feather';
import {useMobile} from '~/hooks';
import {formatNumber} from '~/utils';

import {ProductPlanType} from '~/constants/ProductPlanType';
import {ProductBookingSteps} from '../booking_steps/ProductBookingSteps.client';
import {ProductGiftingSteps} from '../gifting_steps/ProductGiftingSteps.client';
import {IVariantTimeslot} from '~/lib/types/graphql/api';
import {ProductDuration} from './ProductDuration';

interface AddToCartMarkupProps {
  isGift: boolean;
  product: ProductType;
  defaultIsGift: boolean;
  isCheckedGift: boolean;
  planType: ProductPlanType;
  setIsGift: (isCheckedGift: boolean) => void;
  setPlanType: (planType: ProductPlanType) => void;
  onGiftCheckboxChange?: (checked: boolean) => void;
  setIsCheckedGift: (isCheckedGift: boolean) => void;
  isGiftCard: boolean;
}

interface StickBookButtonProps {
  isCheckedGift: boolean;
  isGiftCard: boolean;
}

interface ProductDetailPageProps {
  product: ProductType;
  productRecommendation: ProductType[];
}

interface IFormInput {
  [key: string]: string | boolean;
}

const emailRegexPattern = /^\S+@\S+\.\S+$/;

const cardImages = [
  '/images/gift-cards/01.png',
  '/images/gift-cards/02.png',
  '/images/gift-cards/03.png',
  '/images/gift-cards/04.png',
  '/images/gift-cards/05.png',
  '/images/gift-cards/06.png',
  '/images/gift-cards/07.png',
];

const AddToCartMarkup: FC<AddToCartMarkupProps> = ({
  product,
  isGift,
  planType,
  setIsGift,
  setPlanType,
  defaultIsGift,
  isCheckedGift,
  setIsCheckedGift,
  onGiftCheckboxChange,
  isGiftCard,
}) => {
  const {selectedVariant, selectedSellingPlan} = useProductOptions();
  const availableForSale = selectedVariant?.availableForSale;
  const [openBookingModal, setOpenBookingModal] = useState(false);
  const [openGiftingModal, setOpenGiftingModal] = useState(false);
  // const [openBookingSteps, setOpenBookingSteps] = useState(false);
  const [openGiftingSteps, setOpenGiftingSteps] = useState(false);
  const [isChooseDate, setIsChooseDate] = useState(false);
  const [giftDate, setGiftDate] = useState<Date>(new Date());
  const [slideIndex, setSlideIndex] = useState(0);
  const {setIsOpenCart} = useDrawerContext();
  const {linesAdd} = useCart();
  const isMobile = useMobile();
  // previously we were checking if the product.tags.includes('Gift Card')
  // for now, it is enabled for all products
  const isProductGift = true;

  const handleTriggerBookingModal = () =>
    setOpenBookingModal(!openBookingModal);

  const handleTriggerGiftingModal = () =>
    setOpenGiftingModal(!openGiftingModal);

  const handleTriggerGiftingSteps = () =>
    setOpenGiftingSteps(!openGiftingSteps);

  const handleGiftButtonClick = () => {
    setOpenGiftingModal(false);
    setIsOpenCart(true);
  };

  const {
    register,
    setValue,
    watch,
    formState: {isValid},
  } = useForm<IFormInput>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const [selectedCard, setSelectedCard] = useState(
    '/images/gift-cards/01_HBD GREETING CARD.png',
  );

  useEffect(() => {
    setSelectedCard(cardImages[slideIndex]);
  }, [slideIndex]);

  useEffect(() => {
    setIsCheckedGift(defaultIsGift);
  }, [defaultIsGift, setIsCheckedGift]);

  useEffect(() => {
    const price = selectedVariant
      ? selectedVariant.price
      : product.compareAtPriceRange.minVariantPrice.amount;

    const currency = product.compareAtPriceRange.minVariantPrice.currencyCode;
    const lineItemData = {
      id: product.id,
      name: product.title,
      category: product.productType || 'Uncategorized',
      quantity: 1,
      price,
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.gtag) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.gtag('event', 'view_item', {
        send_to: 'G-SXJN5C8X3L',
        currency, // Use the dynamic currency code
        items: [lineItemData],
      });
    }
  }, []);

  return (
    <>
      <div className="space-y-2">
        {product.productType === 'Beauty Picks' && (
          <AddToCartButton
            className={
              'relative m-0 flex w-full items-center justify-center rounded-full border border-daybreak-400 bg-daybreak-400 px-6 py-4 text-center text-white duration-300 hover:shadow-lg active:bg-daybreak-400 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300'
            }
            variantId={selectedVariant?.id}
            disabled={!availableForSale}
            sellingPlanId={selectedSellingPlan?.id}
            attributes={[
              {
                key: '_tfr_booking',
                value: 'false',
              },
              {
                key: '_tfr_is_gift',
                value: isCheckedGift ? 'true' : 'false',
              },
            ]}
            onClick={() => {
              setIsOpenCart(true);
            }}
          >
            {!availableForSale ? 'Out of Stock' : 'Add to Cart'}
          </AddToCartButton>
        )}
        {product.productType !== 'Beauty Picks' && (
          <>
            {!isGiftCard && (
              <CheckboxField
                label={
                  <span className="flex items-center gap-1">
                    <span className="text-[13px]">Send this as a Gift</span>
                    <span className="mt-[-2px]">
                      <GiftIcon height={20} width={20} />
                    </span>
                  </span>
                }
                labelProps={{
                  className: clsx(
                    'text-[#593B37]',
                    isMobile ? 'justify-center' : '',
                  ),
                }}
                onCheckHandler={(checked) => {
                  setIsCheckedGift(checked);
                  onGiftCheckboxChange?.(checked);
                }}
                defaultChecked={isCheckedGift}
              />
            )}

            <button
              id="btn_book_now"
              className={clsx(
                'book-now-button relative m-0 flex w-full items-center justify-center rounded-full border border-daybreak-400 bg-daybreak-400 px-6 py-4 text-center text-white duration-300 hover:border-white hover:bg-white hover:text-acai-900 active:bg-daybreak-400 active:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300',
                isMobile && 'h-[46px] text-[14px]',
              )}
              onClick={() => {
                if ((isCheckedGift && isProductGift) || isGiftCard) {
                  handleTriggerGiftingModal();
                } else {
                  handleTriggerBookingModal();
                }
              }}
            >
              <div>
                {(isProductGift && isCheckedGift) || isGiftCard
                  ? 'Customize your Gift Message'
                  : 'Book Now'}
              </div>
              {!isMobile && (
                <>
                  <div className="mx-2">
                    <SparkleIcon className="sparkle-icon w-[12px]" />
                  </div>
                  <div>
                    <ProductPrices forBookNow />
                  </div>
                </>
              )}
            </button>
          </>
        )}
      </div>

      <ProductBookingSteps
        isOpen={openBookingModal}
        onClose={() => setOpenBookingModal(false)}
        isGift={isGift}
        setIsGift={setIsGift}
        planType={planType}
        setPlanType={setPlanType}
        product={product}
        onConfirmTimeslot={({productLine, feeLine}) => {
          linesAdd([productLine, ...(feeLine ? [feeLine] : [])]);

          if (selectedVariant) {
            const price = selectedVariant.price?.amount
              ? parseFloat(selectedVariant.price?.amount)
              : 0;
            const currency = selectedVariant.price?.currencyCode; // Get the currency code
            const lineItemData = {
              id: product.id,
              name: product.title,
              category: product.productType || 'Uncategorized',
              quantity: 1,
              price,
            };

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (window.gtag) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // window.gtag('event', 'add_to_cart', {
              //   send_to: 'AW-409867090', // Replace with your Conversion ID and Label
              //   value: lineItemData.price * lineItemData.quantity,
              //   currency, // Use the dynamic currency code
              //   items: [lineItemData],
              // });

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.gtag('event', 'add_to_cart', {
                send_to: 'G-SXJN5C8X3L', // Replace with your Conversion ID and Label
                value: lineItemData.price * lineItemData.quantity,
                currency, // Use the dynamic currency code
                items: [lineItemData],
              });
            }
          }
        }}
      />
      <ProductGiftingSteps
        product={product}
        isGift={isCheckedGift}
        isOpen={openGiftingSteps}
        onClose={() => {
          handleTriggerGiftingSteps();
        }}
      />

      <DefaultModal
        isOpen={openGiftingModal}
        title={isProductGift ? 'Gift a Refresh!' : 'Gift this Service'}
        subtitle={
          'Your recipient will receive an email with redemption instructions. '
        }
        onClose={() => {
          if (isChooseDate) {
            setIsChooseDate(false);
          } else {
            setOpenGiftingModal(false);
          }
        }}
        className={clsx(
          'mx-auto max-w-xl p-0 pb-12 md:my-12 md:p-4',
          isChooseDate ? 'h-full md:h-auto' : '',
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex h-full flex-col items-center gap-4">
            {!isChooseDate ? (
              <div className="grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-2">
                    <div>Recipient Details</div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        className="w-full border border-tundora-800 p-4 focus:outline-none"
                        type="text"
                        placeholder="Name"
                        required
                        {...register('to_name', {required: 'required'})}
                      />
                      <input
                        className="w-full border border-tundora-800 p-4 focus:outline-none"
                        type="email"
                        placeholder="Email"
                        {...register('email', {
                          required: 'Email is required',
                          validate: {
                            matchPattern: (v) =>
                              emailRegexPattern.test(v.toString()) ||
                              'Email address must be a valid address',
                          },
                        })}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div>Sender Details</div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        className="w-full border border-tundora-800 p-4 focus:outline-none"
                        type="text"
                        placeholder="Name"
                        {...register('from_name', {required: true})}
                      />
                      <input
                        className="w-full border border-tundora-800 p-4 focus:outline-none"
                        type="email"
                        placeholder="Email"
                        {...register('from_email', {
                          required: 'Email is required',
                          validate: {
                            matchPattern: (v) =>
                              emailRegexPattern.test(v.toString()) ||
                              'Email address must be a valid address',
                          },
                        })}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 pt-2">
                    <div>Email Send Date</div>
                    <input
                      className="w-full border border-tundora-800 p-4 focus:outline-none"
                      type="text"
                      placeholder="Schedule send date"
                      onClick={() => setIsChooseDate(true)}
                      //Keep it local here
                      value={format(giftDate, 'dd-MM-yyyy')}
                      {...register('date')}
                    />
                  </div>
                  <div className="flex flex-col gap-2 pt-2">
                    <div>Message</div>
                    <textarea
                      className="w-full border border-tundora-800 p-4 focus:outline-none"
                      cols={30}
                      rows={10}
                      placeholder="Hope you enjoy your Refresh :)"
                      defaultValue="Hope you enjoy your Refresh :)"
                      {...register('message')}
                    ></textarea>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="pt-6 text-2xl font-normal text-acai-900">
                    Select a Card
                  </div>
                  <div className="flex w-full items-center justify-center gap-4 py-8">
                    <div>
                      <button
                        onClick={() =>
                          setSlideIndex((prev) =>
                            prev > 0 ? prev - 1 : cardImages.length - 1,
                          )
                        }
                        className={clsx(
                          'flex items-center justify-center rounded-full border border-acai-900 bg-transparent p-1',
                        )}
                      >
                        <ChevronLeft stroke="#593B37" width={12} height={12} />
                      </button>
                    </div>
                    <div className="aspect-square w-60">
                      <Carousel
                        className=""
                        wrapAround={true}
                        slideIndex={slideIndex}
                        slidesToShow={1}
                        cellSpacing={2}
                        withoutControls={true}
                      >
                        {cardImages.map((cardImage, index) => {
                          return (
                            <Image
                              // eslint-disable-next-line react/no-array-index-key
                              key={`card-image-${index}`}
                              className="mx-auto aspect-square cursor-pointer rounded-lg md:h-64 md:w-64"
                              src={cardImage}
                              alt="..."
                              width={180}
                              height={180}
                            />
                          );
                        })}
                      </Carousel>
                    </div>
                    <div>
                      <button
                        onClick={() =>
                          setSlideIndex((prev) =>
                            prev < cardImages.length - 1 ? prev + 1 : 0,
                          )
                        }
                        className={clsx(
                          'flex items-center justify-center rounded-full border border-acai-900 bg-transparent p-1',
                        )}
                      >
                        <ChevronRight stroke="#593B37" width={12} height={12} />
                      </button>
                    </div>
                  </div>
                </div>
                <AddToCartButton
                  type="submit"
                  className={clsx(
                    'w-full rounded-full border-[0.5px] border-[#4B2304] bg-acai-900 py-4 text-sm font-normal text-white disabled:cursor-not-allowed disabled:bg-gray-300',
                  )}
                  variantId={selectedVariant?.id}
                  attributes={[
                    {
                      key: 'Gift Send Date',
                      value: `Your gift to ${
                        watch('to_name') as string
                        //Stays local here
                      } will be sent on ${format(giftDate, 'MMM d yyy')}`,
                    },
                    {
                      key: 'Recipient Email',
                      value: watch('email') as string,
                    },
                    {
                      key: '_start_time',
                      value: giftDate.toISOString(),
                    },
                    //Add an extra attribute which is just the date to send the gift, ensuring timezones dont come into play
                    {
                      key: '_gift_send_date',
                      //Keep it local, so dates dont change
                      value: format(giftDate, 'dd-MM-yyyy'),
                    },
                    {
                      key: '_timezone',
                      value: 'Singapore',
                    },
                    {
                      key: '_tfr_booking',
                      value: 'false',
                    },
                    {
                      key: '_tfr_is_gift',
                      value: isCheckedGift ? 'true' : 'false',
                    },
                    {
                      key: '_gift_to_name',
                      value: watch('to_name') as string,
                    },
                    {
                      key: '_gift_to_email',
                      value: watch('email') as string,
                    },
                    {
                      key: '_gift_from_name',
                      value: watch('from_name') as string,
                    },
                    {
                      key: '_gift_from_email',
                      value: watch('from_email') as string,
                    },
                    {
                      key: '_gift_message',
                      value: watch('message') as string,
                    },
                    {
                      key: '_gift_card',
                      value: selectedCard,
                    },
                  ]}
                  disabled={!isValid}
                  onClick={() => handleGiftButtonClick()}
                >
                  Add to Cart
                </AddToCartButton>
              </div>
            ) : (
              <Calendar
                className="tfr"
                value={giftDate}
                minDate={new Date()}
                onChange={(value: Date) => {
                  setIsChooseDate(false);
                  setGiftDate(value);
                  setValue('date', format(value, 'dd-MM-yyyy'));
                }}
              />
            )}
          </div>
        </div>
      </DefaultModal>
    </>
  );
};

type TProductPrices = {
  hasStartFrom?: boolean;
  forBookNow?: boolean;
  includeSurgeFee?: boolean;
  selectedTimeslot?: IVariantTimeslot;
};

export const ProductPrices: FC<TProductPrices> = ({
  forBookNow = false,
  includeSurgeFee = false,
  selectedTimeslot,
}) => {
  const {selectedVariant, selectedSellingPlan} = useProductOptions();

  let sellingPlanPrice: number | undefined;

  // Use either priceV2 or price, whichever one is available
  const selectedVariantPriceObject =
    selectedVariant?.price ?? selectedVariant?.priceV2;

  const selectedVariantPrice = Number(selectedVariantPriceObject?.amount);

  if (selectedSellingPlan) {
    const adjustmentValue =
      selectedSellingPlan?.priceAdjustments?.[0]?.adjustmentValue;

    // Selling Price = price - fixed amount
    if (
      adjustmentValue &&
      'adjustmentAmount' in adjustmentValue &&
      adjustmentValue.adjustmentAmount
    ) {
      sellingPlanPrice =
        selectedVariantPrice - Number(adjustmentValue.adjustmentAmount.amount);
    }

    // Selling Price = fixed price
    if (
      adjustmentValue &&
      'price' in adjustmentValue &&
      adjustmentValue.price
    ) {
      sellingPlanPrice = Number(adjustmentValue.price.amount);
    }

    // Selling Price = price - (price * percentage)
    if (
      adjustmentValue &&
      'adjustmentPercentage' in adjustmentValue &&
      adjustmentValue.adjustmentPercentage
    ) {
      sellingPlanPrice =
        selectedVariantPrice -
        selectedVariantPrice *
          Number(adjustmentValue.adjustmentPercentage / 100);
    }
  }
  const surgeFee = includeSurgeFee
    ? Number(selectedTimeslot?.surgeFee?.amount ?? 0)
    : 0;

  const shouldInclueSurge =
    selectedSellingPlan && !selectedSellingPlan.recurringDeliveries;

  return (
    <div className={clsx(forBookNow ? '' : 'text-black')}>
      {selectedSellingPlan !== undefined && sellingPlanPrice ? (
        <div className="flex gap-2">
          <span>
            {`${formatNumber(
              shouldInclueSurge
                ? sellingPlanPrice + surgeFee
                : sellingPlanPrice,
            )} ${selectedVariantPriceObject?.currencyCode}`}
          </span>
          <span
            className={clsx([
              forBookNow ? '' : 'text-[#ACACAC]',
              'line-through',
            ])}
          >
            {`${formatNumber(selectedVariantPrice)} ${
              selectedVariantPriceObject?.currencyCode
            }`}
          </span>
        </div>
      ) : (
        `${formatNumber(selectedVariantPrice + surgeFee)} ${
          selectedVariantPriceObject?.currencyCode
        }`
      )}
    </div>
  );
};

function StickBookButton({isCheckedGift, isGiftCard}: StickBookButtonProps) {
  const isProductGift = true;

  const [showStickyButton, setShowStickyButton] = useState<boolean>(false);
  const isMobile = useMobile();

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('btn_book_now');
      const btnPositionY = element?.getBoundingClientRect().bottom ?? 0;
      const headerElement = document.querySelector(
        'header[role="banner"]',
      ) as HTMLElement;
      const headerHeight = headerElement?.offsetHeight ?? 0;
      if (btnPositionY < headerHeight) {
        setShowStickyButton(true);
      } else {
        setShowStickyButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return (
    <div
      className={clsx(
        'sticky-book-button fixed z-20 transform transition-all duration-200',
        showStickyButton
          ? 'bottom-10 translate-y-0 md:bottom-12'
          : 'bottom-0 translate-y-full',
        isMobile ? 'left-8' : '',
      )}
    >
      <button
        className={
          'book-now-button m-0 flex h-[60px] w-full items-center justify-center rounded-full border border-daybreak-400 bg-daybreak-400 px-6 text-center text-white duration-300 hover:border-white hover:bg-white hover:text-acai-900 active:bg-daybreak-400 active:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 sm:h-[80px]'
        }
        onClick={() => {
          const element = document.getElementById('btn_book_now');
          element?.click();
        }}
      >
        <div>
          {(isProductGift && isCheckedGift) || isGiftCard
            ? 'Customize your Gift Message'
            : 'Book Now'}
        </div>
        <div className="mx-2">
          <SparkleIcon className="sparkle-icon min-w-[12px]" />
        </div>
        <div>
          <ProductPrices forBookNow hasStartFrom={isMobile} />
        </div>
      </button>
    </div>
  );
}

export function ProductDetailPage({
  product,
  productRecommendation,
}: ProductDetailPageProps) {
  // previously we were checking if the product.tags.includes('Gift Card')
  // for now, it is enabled for all products
  const isProductGift = true;

  const {isSearching} = useAppContext();

  const [isGift, setIsGift] = useState(product.tags.includes('Gift Card'));
  const [planType, setPlanType] = useState<ProductPlanType>(
    ProductPlanType.Subscription,
  );
  const [isCheckedGift, setIsCheckedGift] = useState(false);
  const isMobile = useMobile();

  // const productTime = product?.productType?.split('|')[0]?.trim() ?? '';
  const isGiftCard = useMemo(
    () => product.tags.includes('Gift Card'),
    [product],
  );

  return (
    <ProductOptionsProvider data={product}>
      {isSearching && (
        <div className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-[rgba(255,255,255,0.25)]">
          <RefreshingTimeslots />
        </div>
      )}
      <StickBookButton isCheckedGift={isCheckedGift} isGiftCard={isGiftCard} />
      <div
        className={clsx(
          isSearching && 'grayscale',
          'transition-all duration-200',
          'mt-20 md:mt-0',
        )}
      >
        <div
          className={
            'grid grid-cols-1 bg-foam-100 pb-8 pt-[103px] md:grid-cols-2 md:pb-16'
          }
        >
          <div className={clsx(isMobile && 'mx-8 overflow-hidden rounded-lg')}>
            <Gallery product={product} />
          </div>
          <div className="mx-8 my-6 flex flex-col gap-4 md:mx-20">
            <div className="flex flex-col gap-2">
              <h1 className="text-4xl text-black">{product.title}</h1>
              <ProductReview product={product as ProductType} />
              <div className="flex items-center gap-2">
                <ProductPrices hasStartFrom={isProductGift} />
                {product.productType !== 'Beauty Picks' && <ProductDuration />}
              </div>
              {product.handle === 'gift-a-refresh' && <ProductOptions />}
            </div>
            <AddToCartMarkup
              isGift={isGift}
              product={product}
              planType={planType}
              setIsGift={setIsGift}
              defaultIsGift={isGift}
              setPlanType={setPlanType}
              isCheckedGift={isCheckedGift}
              setIsCheckedGift={setIsCheckedGift}
              isGiftCard={isGiftCard}
            />
            <ProductDescriptionTab product={product} />
            <div>
              <Link
                to={'/faq?menu=services'}
                className={clsx(
                  'text-daybreak-400 underline',
                  product.productType === 'Beauty Picks' ? 'hidden' : 'block',
                )}
              >
                Frequently Asked Questions
              </Link>
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center">
          {/* TODO: Fix product review */}
          <ProductDetailBanner product={product} />
          <ProductAssurance />
          <ProductRecommendation products={productRecommendation} />
          <ProductDetailReview product={product} />
        </div>
      </div>
    </ProductOptionsProvider>
  );
}
